import React from 'react';
import { Star, PlayCircle } from 'lucide-react';
import {
  CQC_HEADLINE,
  CQC_METRICS_LEFT,
  CQC_STORY_PARAGRAPHS,
  CQC_VIDEO_EMBED_URL,
} from '../../StartCQCHome';
import {
  welcomeGlass,
  welcomeEyebrow,
  welcomeSectionTitle,
  welcomeBodyText,
} from '../welcomeTokens';

// Awards, employees and revenue only; the /start page keeps all six.
const METRICS = CQC_METRICS_LEFT;

/**
 * "Our Story" popup for /welcome/JTBootcamp — same content as the /start
 * subpage (imported, never duplicated), restyled into the welcome page's dark
 * glass language. The /start version is untouched.
 */
const WelcomeStoryPanel = () => (
  <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-8 sm:px-6 sm:py-10">
    <section className="relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600 to-red-800 px-6 py-9 text-center shadow-[0_18px_50px_-24px_rgba(0,0,0,0.9)] sm:px-10">
      <div
        className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl"
        aria-hidden
      />
      <div className="relative flex flex-col items-center">
        <img
          src="/cqclogo.png"
          alt="CQC Home"
          className="h-28 w-auto object-contain sm:h-32"
        />
        <p className="mt-6 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm">
            <Star className="h-3.5 w-3.5 fill-current" aria-hidden />
            Fully Management Run
          </span>
        </p>
        <h2 className="mt-5 max-w-2xl text-center text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
          {CQC_HEADLINE}
        </h2>
        <p className="mt-3 text-center text-sm font-medium text-red-100">
          One defined, proven model — and the systems that run it.
        </p>
      </div>
    </section>

    <section>
      <h3 className={`${welcomeEyebrow} text-center`}>By the numbers</h3>
      <dl className="mt-4 grid grid-cols-3 gap-3">
        {METRICS.map((metric) => (
          <div
            key={metric.label}
            className={`rounded-2xl px-3 py-5 text-center ${welcomeGlass}`}
          >
            <div className="text-2xl leading-none" aria-hidden="true">
              {metric.emoji}
            </div>
            <dd className="mt-2 text-2xl font-extrabold tabular-nums text-red-400">
              {metric.value}
            </dd>
            <dt className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
              {metric.label}
            </dt>
          </div>
        ))}
      </dl>
    </section>

    <section className={`relative overflow-hidden rounded-2xl p-6 sm:p-8 ${welcomeGlass}`}>
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent"
        aria-hidden
      />
      <h3 className={welcomeSectionTitle}>How CQC Home Got Here</h3>
      <div className="mt-6 space-y-5">
        {CQC_STORY_PARAGRAPHS.map((paragraph, i) => (
          <p key={i} className={welcomeBodyText}>
            {paragraph}
          </p>
        ))}
      </div>
    </section>

    <section className={`rounded-2xl p-6 sm:p-8 ${welcomeGlass}`}>
      <h3 className={`${welcomeSectionTitle} flex items-center justify-center gap-2`}>
        <PlayCircle className="h-5 w-5 text-red-500" aria-hidden />
        Learn More About CQC Home
      </h3>
      <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-black">
        <iframe
          src={CQC_VIDEO_EMBED_URL}
          title="Learn more about CQC Home"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="aspect-video w-full"
        />
      </div>
    </section>
  </div>
);

export default WelcomeStoryPanel;
